import { Canvas, useThree } from "@react-three/fiber";
import { Suspense, useLayoutEffect } from "react";
import { OrthographicCamera as OrthographicCameraImpl } from "three";
import { VehicleModel } from "./VehicleModel";
import type { VehicleDef } from "./vehicles";

/** Orthographic framing — zoomed out so the full podium stays visible. */
function AimOrthoCamera() {
  const { set } = useThree();
  useLayoutEffect(() => {
    // Extra bottom margin so the cylinder rim isn't clipped.
    const cam = new OrthographicCameraImpl(-1.7, 1.7, 1.35, -1.05, 0.1, 20);
    cam.position.set(1.05, 1.35, 2.55);
    cam.lookAt(0, 0.25, 0);
    cam.updateProjectionMatrix();
    // Without this, R3F overwrites the frustum to ±canvasPixels/2 and the
    // car shrinks to a speck (~0.5% of the stage).
    (cam as OrthographicCameraImpl & { manual: boolean }).manual = true;
    set({ camera: cam });
  }, [set]);
  return null;
}

/** Isolated spinning vehicle for the garage panel (not on the race track). */
export function GaragePreview({ vehicle }: { vehicle: VehicleDef }) {
  return (
    <div className="garage-stage" aria-hidden>
      <Canvas
        orthographic
        camera={{ position: [1.05, 1.35, 2.55], zoom: 1, near: 0.1, far: 20 }}
        dpr={[1, 1.5]}
        gl={{
          antialias: true,
          alpha: true,
          powerPreference: "low-power",
          preserveDrawingBuffer: true,
        }}
      >
        <color attach="background" args={["#0a1020"]} />
        <AimOrthoCamera />
        <ambientLight intensity={0.85} />
        <directionalLight position={[2.5, 5, 2]} intensity={1.3} color="#E0F2FE" />
        <pointLight position={[0, 1.8, 1.2]} intensity={0.85} color="#FFFFFF" distance={8} />

        <mesh position={[0, 0.04, 0]}>
          <cylinderGeometry args={[1.0, 1.06, 0.08, 48]} />
          <meshStandardMaterial
            color="#111827"
            emissive={vehicle.emissive}
            emissiveIntensity={0.22}
            roughness={0.85}
          />
        </mesh>
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.081, 0]}>
          <ringGeometry args={[0.94, 1.1, 48]} />
          <meshBasicMaterial color={vehicle.color} transparent opacity={0.55} />
        </mesh>

        <Suspense fallback={null}>
          {/* Podium top ≈ 0.08. groundBias=0 keeps AABB plant — do not sink. */}
          <group position={[0, 0.08, 0]}>
            <VehicleModel key={vehicle.id} vehicle={vehicle} spin groundBias={0} />
          </group>
        </Suspense>
      </Canvas>
    </div>
  );
}
